/* Dark Forest B2B funnel instrumentation — b2b-measurement-2026-09-18.
   Loaded BEFORE b2b-r8-20260905-app.js (same <head>, both `defer`, document order), so the
   fetch patch below installs before the app makes its first network call.
   No PII is read or sent: only route, country (ISO-2, a shipping destination, not an
   address), quantity, money amounts and currency, and buyer_type ("company"/"individual").
   Every event is mirrored into Shopify.analytics.publish (best-effort, for the two existing
   custom pixels, code unread) AND fired directly into the gtag.js runtime already loaded on
   this page for the Google Ads accounts (this GA4 property becomes a second destination on
   that SAME loaded gtag.js — no second script tag, no separate consent handling) AND into the
   Meta pixel already initialised on this page. Both are queued until that runtime says it
   exists (denied-consent / not-yet-loaded sessions), for up to ~6s, then dropped for that call.

   Ground-truth policy, set after this run's own proof walk (2026-09-18): the app's live
   validation state (#error-quantity, #error-country) is NOT populated until "Proceed to
   payment" is clicked — the estimate panel losing its price is the only signal available
   while typing, so quantity/country valid-vs-rejected is read from the ESTIMATE PANEL,
   settled via MutationObserver rather than a fixed delay, never from those error elements
   alone. #error-* text is still read to classify the REASON when Proceed to payment has
   already been pressed once. */
(function () {
  'use strict';
  if (window.__dfB2BAnalyticsInit) return;
  window.__dfB2BAnalyticsInit = true;

  var GA4_ID = 'G-MW3DLYYT3X';
  var ga4Registered = false;
  var startedFired = false;
  var last = { quantity: null, quantityState: null, country: null, countryState: null, total: null };

  function routeFromUrl() {
    try {
      var p = new URLSearchParams(location.search);
      var r = p.get('route');
      if (r) return r;
    } catch (e) {}
    var page = document.body && document.body.dataset ? document.body.dataset.page : '';
    if (page === 'catalog') return 'stock';
    if (page === 'printing') return 'own_artwork';
    return page || 'unknown';
  }
  function currentCountry() {
    var el = document.querySelector('#country, #review-country, [name="country"]');
    return el ? el.value : '';
  }
  function currentQuantity() {
    var el = document.querySelector('#quantity, [name="quantity"]');
    return el ? el.value : null;
  }
  function currentBuyerType() {
    var el = document.querySelector('#buyer_type, [name="buyer_type"]');
    return el ? el.value : null;
  }
  // NOT window.Shopify.currency.active - proof-walked 2026-09-18 (independent QA re-check): the
  // B2B app always prices in USD (its own JSON-LD says so, every panel figure is "$"), but that
  // Shopify field reflects the VISITOR's account/market currency and can read "EUR" on the same
  // page that visibly shows "$357.60" - reporting that pair together as "357.60 EUR" is a real
  // mislabel, not a hypothetical one. Read the symbol the panel itself is actually showing instead.
  function currency() {
    var box = estimateBox();
    var totalNode = box ? box.querySelector('.total .money') : null;
    var sym = totalNode ? totalNode.textContent.trim().charAt(0) : '$';
    if (sym === '€') return 'EUR';
    if (sym === '£') return 'GBP';
    return 'USD';
  }
  function moneyToNumber(text) {
    if (!text) return null;
    var n = parseFloat(String(text).replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? null : n;
  }
  function estimateBox() { return document.querySelector('#estimate, #b2b-quote-review'); }
  function estimateIsBlank() {
    var box = estimateBox();
    if (!box) return false;
    if (/quantity not selected|enter your quantity/i.test(box.textContent)) return true;
    return !box.querySelector('.total .money');
  }
  // Ground truth for prices: read the app's OWN rendered estimate, never re-derive the math.
  function readEstimate() {
    var box = estimateBox();
    var out = { goods: null, delivery: null, total: null };
    if (!box) return out;
    var lines = box.querySelectorAll('.line');
    for (var i = 0; i < lines.length; i++) {
      var label = lines[i].querySelector('span:first-child');
      var val = lines[i].querySelector('.money');
      if (!label || !val) continue;
      var t = label.textContent.trim().toLowerCase();
      if (t.indexOf('subtotal') !== -1) out.goods = moneyToNumber(val.textContent);
      if (t.indexOf('shipping') !== -1 || t.indexOf('delivery') !== -1) out.delivery = moneyToNumber(val.textContent);
    }
    var totalNode = box.querySelector('.total .money');
    out.total = totalNode ? moneyToNumber(totalNode.textContent) : null;
    // At the delivery/review stage a second, final total appears once shipping is priced
    // ("$226.80 incl. insured delivery") — prefer it over the goods-only total when present.
    var full = document.body.textContent.match(/\$[0-9,]+\.\d{2}\s*incl\.?\s*insured delivery/i);
    if (full) {
      var n = moneyToNumber(full[0]);
      if (n != null) out.total = n;
    }
    return out;
  }
  function ladderReason(v) {
    var n = Number(v);
    if (v === '' || isNaN(n) || !/^\d+$/.test(String(v).trim())) return 'non_numeric';
    if (n < 12) return 'below_min';
    if (n > 192) return 'above_max';
    if (n % 12 !== 0) return 'off_ladder';
    return 'other';
  }
  // Waits for #estimate to read the SAME state N times in a row (poll every 250ms), AFTER first
  // waiting for any in-flight calculator request to finish - proof-walked 2026-09-18, THREE
  // TIMES: the panel flickers through an interim "recalculating" blank frame before its real
  // value lands; a fixed delay, a single MutationObserver quiet-window, a symmetric two-read
  // check, and even an ASYMMETRIC 6-reads-for-blank check (1.5s) each in turn read a genuinely
  // valid quantity as rejected, because some quantities recompute purely client-side (fast, no
  // network call at all) while others cross a pricing tier and call the calculator API with a
  // round trip that can run longer than any fixed window this script can reasonably guess (48
  // decks broke the 1.5s asymmetric version that had already fixed 24 and 36 - independent
  // qa-verifier re-check, not this run's own walk). No amount of polling alone can distinguish
  // "still loading" from "rejected" without knowing whether a network call is actually pending.
  // pendingCalcRequests (set by the fetch intercept below) answers that directly: wait for it to
  // clear FIRST, THEN read the DOM - this is the fix, not a bigger number.
  function waitForEstimateStable(cb) {
    var attempts = 0, maxAttempts = 40, stableCount = 0, prevSig = null; // hard cap 10s
    function tick() {
      attempts++;
      if (pendingCalcRequests > 0) {
        // a real round trip is in flight right now - any reading taken mid-flight is worthless,
        // so drop whatever streak was building and wait for it to clear before counting again.
        stableCount = 0; prevSig = null;
        if (attempts >= maxAttempts) { cb(); return; }
        setTimeout(tick, 250);
        return;
      }
      var blankNow = estimateIsBlank();
      var sig = blankNow ? 'blank' : String(readEstimate().total);
      if (sig === prevSig) stableCount++; else { stableCount = 1; prevSig = sig; }
      var needed = blankNow ? 4 : 2;
      if (stableCount >= needed || attempts >= maxAttempts) { cb(); return; }
      setTimeout(tick, 250);
    }
    tick();
  }

  // ---- dispatch: Shopify.analytics.publish (best-effort) + gtag + fbq, queued until ready ----
  var pending = [];
  var flushTimer = null;
  function tryFlush() {
    var stillWaiting = false;
    pending = pending.filter(function (item) {
      if (item.needs === 'gtag' && typeof window.gtag !== 'function') { stillWaiting = true; return true; }
      if (item.needs === 'fbq' && typeof window.fbq !== 'function') { stillWaiting = true; return true; }
      try { item.fn(); } catch (e) {}
      return false;
    });
    if (stillWaiting && !flushTimer) flushTimer = setTimeout(function () { flushTimer = null; tryFlush(); }, 500);
  }
  setTimeout(function () { pending = []; }, 6000);

  function track(name, params) {
    params = params || {};
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        var r = window.Shopify.analytics.publish('b2b/' + name, params);
        if (r && typeof r.catch === 'function') r.catch(function () {});
      }
    } catch (e) {}
    pending.push({ needs: 'gtag', fn: function () {
      if (!ga4Registered) { window.gtag('config', GA4_ID, { send_page_view: false }); ga4Registered = true; }
      window.gtag('event', name, params);
    }});
    pending.push({ needs: 'fbq', fn: function () { window.fbq('trackCustom', name, params); } });
    tryFlush();
  }
  function markStarted() {
    if (startedFired) return;
    startedFired = true;
    track('b2b_configurator_started', { route: routeFromUrl(), page: location.pathname });
  }

  // Tracks whether a pricing round trip is genuinely in flight right now - some quantity edits
  // recompute purely client-side from an already-fetched price table (no new request at all;
  // proof-walked 2026-09-18) while OTHERS - proof-walked again, independently, by qa-verifier -
  // cross a pricing tier and DO call the calculator API, and can take longer than any fixed poll
  // window this script could reasonably guess (48 decks reproduced a false rejection at a 1.5s
  // blank-confirmation threshold that had already fixed 24 and 36). Waiting on the request itself,
  // when one exists, replaces guessing at how long its round trip takes.
  var pendingCalcRequests = 0;

  // ---- fetch intercept: only for the two events that ARE a distinct, unambiguous network call ----
  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var promise = origFetch.apply(this, arguments);
      try {
        if (/\/b2b-api\/api\/calculator\//i.test(url)) {
          pendingCalcRequests++;
          promise.then(function () { pendingCalcRequests--; }, function () { pendingCalcRequests--; });
        }
        if (/\/b2b-api\/api\/calculator\/|\/apps\/b2b-orders\/api\/orders\/v1\/projects/i.test(url)) {
          markStarted();
        }
        if (/\/apps\/b2b-orders\/api\/orders\/v1\/projects\/[^/]+\/checkout$/i.test(url) && method === 'POST') {
          promise.then(function (res) {
            if (!res || !res.ok) return;
            var est = readEstimate();
            track('b2b_proceed_to_payment', {
              route: routeFromUrl(), quantity: currentQuantity(), country: currentCountry(),
              total: est.total, currency: currency(), buyer_type: currentBuyerType()
            });
          }).catch(function () {});
        }
      } catch (e) {}
      return promise;
    };
  }

  // ---- unified DOM-settle state machine for quantity / country / estimate ----
  // One settle pass reads quantity + country + estimate together and fires whichever of
  // quantity_set, quantity_rejected, country_chosen, country_unsupported, estimate_ready
  // actually changed since the last pass - avoids firing the same event twice for one edit
  // and avoids the fetch layer entirely for fields the app resolves without a network call.
  var settleTimer = null;
  function scheduleSettle() {
    markStarted();
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () { waitForEstimateStable(evaluate); }, 300);
  }
  function evaluate() {
    var qVal = currentQuantity();
    var cVal = currentCountry();
    var blank = estimateIsBlank();
    var errQ = document.querySelector('#error-quantity');
    var errQText = errQ ? errQ.textContent.trim() : '';

    if (qVal !== last.quantity) {
      last.quantity = qVal;
      if (qVal) {
        if (blank || errQText) {
          var reason = errQText ? 'other' : ladderReason(qVal);
          if (errQText) {
            if (/multiples of|ladder|nearby valid/i.test(errQText)) reason = 'off_ladder';
            else if (/larger run|maximum|above|200/i.test(errQText)) reason = 'above_max';
            else if (/numeric|number|abc/i.test(errQText)) reason = 'non_numeric';
            else if (/minimum|at least|below/i.test(errQText)) reason = 'below_min';
          }
          last.quantityState = 'rejected';
          track('b2b_quantity_rejected', { route: routeFromUrl(), entered: qVal, reason: reason });
        } else {
          last.quantityState = 'valid';
          track('b2b_quantity_set', { route: routeFromUrl(), quantity: qVal, valid: true });
        }
      }
    }

    if (cVal !== last.country) {
      last.country = cVal;
      if (cVal === 'OTHER') {
        var otherEl = document.querySelector('#other-country');
        last.countryState = 'unsupported';
        track('b2b_country_unsupported', { country_typed: (otherEl && otherEl.value) || '', route: routeFromUrl() });
      } else if (cVal) {
        var errC = document.querySelector('#error-country');
        if (!(errC && errC.textContent.trim())) {
          last.countryState = 'chosen';
          track('b2b_country_chosen', { country: cVal, route: routeFromUrl() });
        }
      }
    }

    if (!blank && cVal && cVal !== 'OTHER' && currentBuyerType()) {
      var est = readEstimate();
      if (est.total != null && est.total !== last.total) {
        last.total = est.total;
        track('b2b_estimate_ready', {
          route: routeFromUrl(), quantity: qVal, country: cVal,
          goods: est.goods, delivery: est.delivery, total: est.total, currency: currency()
        });
      }
    }
  }

  ['input', 'change'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var t = e.target;
      if (!t || !t.id) return;
      if (t.id === 'quantity' || t.id === 'country' || t.id === 'buyer_type' || t.id === 'review-country') {
        scheduleSettle();
      }
    }, true);
  });
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('button, a') : null;
    if (t) markStarted();
  }, true);

  // ---- the quote/contact form's server-rendered success state (kb/b2b-quote-instrumentation.md) ----
  function checkQuoteSuccess() {
    if (!/contact_posted=true/.test(location.search)) return;
    var ok = document.querySelector('#b2b-question-status.success');
    if (ok) track('b2b_quote_form_submitted', { page: location.pathname });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', checkQuoteSuccess);
  else checkQuoteSuccess();
})();
